import { useCallback, useMemo } from 'react';

import { useQuery } from '@apollo/client/react';
import { ListItem } from '@mui/material';
import { useSearchParams } from 'react-router-dom';

import {
  type Lead_Stages,
  type Leads_Bool_Exp,
  Order_By,
} from '@graphql/__generated__/graphql';
import {
  type QuickFilterDefinition,
  deleteWhereClause,
} from '@modules/_shared/components/filters/Filters';
import type { filterAutocompleteRenderOptionProps } from '@modules/_shared/components/filters/quick-filters/QuickFilters';
import { IndicatorLeadStage } from '@modules/_shared/components/IndicatorLeadStage';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { getAgentFilter } from '@modules/_shared/utils/quickFilters';
import { GET_PIPELINE_STAGES } from '@modules/leads/leadsQueries';
import { useLocale } from '@shared/hooks/locale';

export const useLeadsQuickFilters = ({
  excludeSource = false,
  pipelineId: pipelineIdProp,
}: {
  excludeSource?: boolean;
  pipelineId?: string | null;
} = {}): QuickFilterDefinition[] => {
  const { t } = useLocale();
  const { me } = useAppData();
  const [searchParams] = useSearchParams();
  const viewType = searchParams.get('view') ?? 'list';
  const isTenantSpanish = me?.tenant.country_code === 'ES';

  const pipelineId =
    pipelineIdProp !== undefined
      ? pipelineIdProp
      : searchParams.get('pipelineId_eq') ??
        me?.tenant.default_pipeline_id ??
        null;

  const { data: stagesData } = useQuery(GET_PIPELINE_STAGES, {
    variables: { pipeline_id: pipelineId ?? '' },
    skip: pipelineId == null,
  });

  const renderLeadStage = useCallback<filterAutocompleteRenderOptionProps>(
    (props, option) => {
      const status = stagesData?.pipelines_by_pk?.stages.find(
        stage => stage.label === option.value,
      )?.status;

      return (
        <ListItem {...props} key={`option-${option.value}`}>
          <IndicatorLeadStage status={status}>
            {option.label}
          </IndicatorLeadStage>
        </ListItem>
      );
    },
    [stagesData],
  );

  return useMemo<QuickFilterDefinition[]>(() => {
    const filters: (QuickFilterDefinition | null)[] = [
      {
        label: t('Date'),
        path: ['leads_bool_exp', 'requalified_or_created_at'],
        dateFields: [
          {
            value: 'requalified_or_created_at',
            label: t('Requalification or creation'),
          },
          { value: 'requalified_at', label: t('Requalification') },
          { value: 'created_at', label: t('Creation') },
        ],
      },
      {
        label: t('Source'),
        path: ['leads_bool_exp', 'source', 'name', '_in'],
        displayedColumn: 'label',
        filter: {
          path: ['source', 'type', '_in'],
          value: 'lead_source_types',
        },
      },
      viewType === 'kanban'
        ? null
        : {
            label: t('Pipeline step'),
            path: ['leads_bool_exp', 'lead_agents', 'lead_stage', 'id', '_in'],
            displayedColumn: 'label',
            filter: {
              path: ['lead_stages', 'pipeline', 'name', '_eq'],
              value: 'brokerage',
            },
            orderBy: {
              order_nr: Order_By.AscNullsLast,
            } satisfies Partial<Record<keyof Lead_Stages, Order_By>>,
            filterAutocompleteRenderOption: renderLeadStage,
          },
      {
        label: t('Status'),
        path: ['leads_bool_exp', 'lead_agents', 'lead_stage', 'status', '_in'],
        shouldDisplay: (where: Leads_Bool_Exp) =>
          where._not?.lead_agents?.user == null,
      },
      {
        label: t('Status'),
        path: [
          'leads_bool_exp',
          '_not',
          'lead_agents',
          'lead_stage',
          'status',
          '_in',
        ],
        excludeMode: true,
        shouldDisplay: (where: Leads_Bool_Exp) =>
          where._not?.lead_agents?.user != null,
      },
      {
        label: t('Completed'),
        path: ['leads_bool_exp', 'completed'],
      },
      {
        label: t('Relation'),
        path: ['leads_bool_exp', 'relationship', '_in'],
      },
      {
        label: t('Appraisal Reason'),
        path: ['leads_bool_exp', 'appraisal_reason', '_in'],
      },
      {
        label: t('Sale Horizon'),
        path: ['leads_bool_exp', 'sale_horizon', '_in'],
      },
      {
        label: t('Contact'),
        path: ['leads_bool_exp', 'contact'],
      },
      {
        label: t('Broker'),
        path: ['leads_bool_exp', 'lead_agents', 'user'],
        filter: getAgentFilter(me),
        onChange: (value: string[] | undefined, whereClause: {}) => {
          if (value?.length === 1 && value[0] == null) {
            return deleteWhereClause(
              whereClause,
              ['leads_bool_exp', 'lead_agents', 'lead_stage', 'status', '_in'],
              true,
            );
          }

          return deleteWhereClause(
            whereClause,
            [
              'leads_bool_exp',
              '_not',
              'lead_agents',
              'lead_stage',
              'status',
              '_in',
            ],
            true,
          );
        },
      },
      {
        label: t('Team'),
        path: ['leads_bool_exp', 'lead_agents', 'user', 'teams_users', 'team'],
      },
      {
        label: t('Location'),
        path: ['leads_bool_exp', 'property', 'places'],
      },
      {
        label: t('value'),
        path: ['leads_bool_exp', 'property', 'latest_appraisal', 'value'],
      },
      {
        label: t('Mandate Probability'),
        path: ['leads_bool_exp', 'lead_agents', 'win_probability'],
      },
      {
        label: t('Predicted Listing Date'),
        path: ['leads_bool_exp', 'lead_agents', 'predicted_win_date'],
      },
      {
        label: t('Tags'),
        path: ['leads_bool_exp', 'leads_tags', 'dictionary', 'name', '_in'],
        displayedColumn: 'label',
        filter: {
          path: ['dictionaries', 'type', '_in'],
          value: 'tags_types',
        },
      },
      {
        label: t('Property type'),
        path: ['leads_bool_exp', 'property', 'property_type', 'name', '_in'],
        displayedColumn: 'label',
        filter: {
          path: ['property_type', 'main_type', '_is_null'],
          value: false,
        },
      },
      {
        label: t('Number of rooms'),
        path: ['leads_bool_exp', 'property', 'number_of_rooms'],
        shouldDisplay: () => !isTenantSpanish,
      },
      {
        label: t('Number of bedrooms'),
        path: ['leads_bool_exp', 'property', 'number_of_bedrooms'],
        shouldDisplay: () => isTenantSpanish,
      },
      {
        label: t('Land surface'),
        path: ['leads_bool_exp', 'property', 'land_surface'],
      },
      {
        label: t('Living surface'),
        path: ['leads_bool_exp', 'property', 'living_surface'],
        shouldDisplay: () => !isTenantSpanish,
      },
      {
        label: t('Built surface'),
        path: ['leads_bool_exp', 'property', 'built_surface'],
        shouldDisplay: () => isTenantSpanish,
      },
      {
        label: t('Has overdue activity'),
        path: ['leads_bool_exp', 'next_activity', 'is_overdue'],
        isNullable: false,
      },
    ];
    return filters.filter(
      (v): v is QuickFilterDefinition =>
        v != null && !(excludeSource && v.path[1] === 'source'),
    );
  }, [t, renderLeadStage, viewType, isTenantSpanish, me, excludeSource]);
};
